import { PollingMode, User } from "configcat-common";
import { IConfigCatClient } from "configcat-common/lib/ConfigCatClient";
import { getClient } from "configcat-js";

import { safeLocalStorage } from "./safeStorage";

const CONFIGCAT_USER_ID_KEY = "configcat_anonymous_id";

// Generate a stable anonymous ID for ConfigCat that persists across sessions
function getConfigCatUserId(): string {
  // Check if we already have a ConfigCat user ID in storage
  let configCatUserId = safeLocalStorage.getItem(CONFIGCAT_USER_ID_KEY);

  if (!configCatUserId) {
    const uuid =
      typeof globalThis.crypto?.randomUUID === "function"
        ? globalThis.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    configCatUserId = `cc-${uuid}`;
    safeLocalStorage.setItem(CONFIGCAT_USER_ID_KEY, configCatUserId);
  }

  return configCatUserId;
}

export enum FlagsStatus {
  LOADING = "loading",
  FAILED = "failed",
  SUCCESS = "success",
}

export const FEATURE_FLAGS = {
  AFFILIATE_DESTINATIONS: "affiliateDestinations",
  AI_MEDICATION_ENTRY: "aiMedicationEntry",
  COVID_AI_MEDICATIONS: "covidAIMedications",
  VIKING_T2D_AI_MEDICATIONS: "vikingT2dAIMedications",
  MINIMATCH_MATS_AB_TEST: "minimatchMatsAbTest",
  USE_KATCH_FOR_ELIGIBILITY: "useKatchForEligibility",
  EMR_DEFERRED_CONSENT: "emrDeferredConsent",
};

type FlagValue = unknown;
export type Flags = Map<string, FlagValue>; // `undefined` means the flags didn't load.

export interface FlagState {
  configCatClient: IConfigCatClient;
  flagValues: Flags;
  status: FlagsStatus;
}

// Determine if we're in a production environment
export const isProduction = import.meta.env.PROD === true;

// Debug function for non-production environments
export const debugLogFlag = (key: string, value: FlagValue): FlagValue => {
  if (!isProduction) {
    console.log(`[ConfigCat] Flag loaded: ${key} = ${JSON.stringify(value)}`);
  }
  return value;
};

export const getFlagValues = async ({
  client,
  landingPageId,
  variantId,
}: {
  client: IConfigCatClient;
  landingPageId?: string;
  variantId?: string;
}): Promise<Flags> => {
  // Use ConfigCat-specific user ID for consistent targeting
  const userId = getConfigCatUserId();

  const user = new User(userId, undefined, undefined, {
    ...(landingPageId ? { landingPageRootId: landingPageId } : {}),
    ...(variantId ? { landingPageVariantId: variantId } : {}),
  });

  if (!isProduction) {
    if (landingPageId)
      console.log(`[ConfigCat] Using landingPageRootId: ${landingPageId}`);
    if (variantId)
      console.log(`[ConfigCat] Using landingPageVariantId: ${variantId}`);
  }

  const flagKeys = await client.getAllKeysAsync();
  if (!isProduction) {
    console.log(`[ConfigCat] Loading ${flagKeys.length} feature flags`);
  }

  // Each flag name/key maps to [key, value]
  const flagValues = flagKeys.map(async (key) => {
    const value = await client.getValueAsync(key, undefined, user);
    return [key, debugLogFlag(key, value)];
  });

  return new Map<string, FlagValue>(
    // Wait for all flag values to resolve.
    (await Promise.all(flagValues)) as Array<[string, FlagValue]>,
  );
};

export const refreshFlagState = async ({
  client,
  landingPageId,
  variantId,
}: {
  client: IConfigCatClient;
  landingPageId?: string;
  variantId?: string;
}): Promise<FlagState> => {
  let status = FlagsStatus.SUCCESS;

  try {
    await client.forceRefreshAsync();
    const flagValues = await getFlagValues({ client, landingPageId, variantId });

    // ConfigCat SDK doesn't allow us to differentiate between error getting flags and no flags existing.
    if (flagValues.size === 0) {
      status = FlagsStatus.FAILED;
      console.error("[ConfigCat] Failed to load any flags - this may affect A/B test assignments");
    } else {
      // Log successful flag loading with key metrics
      const userId = getConfigCatUserId();
      console.log(`[ConfigCat] Successfully loaded ${flagValues.size} flags for user: ${userId}`);
    }

    return { configCatClient: client, flagValues, status };
  } catch (error) {
    console.error("[ConfigCat] Error refreshing flag state:", error);
    status = FlagsStatus.FAILED;
    return { configCatClient: client, flagValues: new Map(), status };
  }
};

export interface ConfigCatConfig {
  sdkKey?: string;
  timeout: number;
}

export const getConfigCatConfig = (): ConfigCatConfig => ({
  sdkKey: import.meta.env.VITE_CONFIG_CAT_KEY as string | undefined,
  // 1.2s is typical maximum if you're, say, the opposite side of the planet from the singly-homed flag server...
  timeout: 2000,
});

export const initConfigCat = ({
  sdkKey,
  timeout,
}: ConfigCatConfig & Required<Pick<ConfigCatConfig, "sdkKey">>): FlagState => {
  if (!isProduction) {
    console.log(
      `[ConfigCat] Initializing with SDK key: ${sdkKey.substring(0, 5)}...`,
    );
  }

  const configCatClient = getClient(sdkKey, PollingMode.AutoPoll, {
    requestTimeoutMs: timeout,
  });
  return {
    configCatClient,
    flagValues: new Map<string, unknown>(),
    status: FlagsStatus.LOADING,
  };
};

// Export the type for use in featureFlagProvider.tsx
export type FlagStateOptionalClient = Omit<FlagState, "configCatClient"> &
  Partial<Pick<FlagState, "configCatClient">>;
